import React, { useState, useEffect } from 'react';
import { Bell, Send, RefreshCw, Users } from 'lucide-react';
import { notificationsAPI, usersAPI } from '../../services/api';
import { useProgram } from '../../contexts/ProgramContext';
import AdminLayout from '../../components/admin/AdminLayout';
import '../../styles/pages/NotificationsPage.css';
import '../../styles/admin-shared.css';

const NotificationsPage = () => {
  // Re-fetch on program switch
  const { slug } = useProgram();
  const [formData, setFormData] = useState({
    target: 'all', // 'all', 'user', 'topic'
    userId: '',
    topic: '',
    title: '',
    body: '',
    type: 'SYSTEM',
    related_id: '',
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadUsers();
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getAll({ limit: 1000 });
      setUsers(response.data.data.users || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const loadLogs = async () => {
    try {
      setLogsLoading(true);
      const response = await notificationsAPI.getLogs({ limit: 50 });
      setLogs(response.data.data.logs || []);
    } catch (error) {
      console.error('Erro ao carregar logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.body) {
      setMessage({ type: 'error', text: 'Título e corpo são obrigatórios' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      let response;
      
      if (formData.target === 'all') {
        response = await notificationsAPI.sendToAll({
          title: formData.title,
          body: formData.body,
          type: formData.type,
          data: formData.related_id ? { related_id: formData.related_id } : {},
        });
      } else if (formData.target === 'user') {
        if (!formData.userId) {
          setMessage({ type: 'error', text: 'Selecione um usuário' });
          setLoading(false);
          return;
        }
        response = await notificationsAPI.sendToUser(formData.userId, {
          title: formData.title,
          body: formData.body,
          type: formData.type,
          related_id: formData.related_id,
        });
      } else if (formData.target === 'topic') {
        if (!formData.topic) {
          setMessage({ type: 'error', text: 'Informe o tópico' });
          setLoading(false);
          return;
        }
        response = await notificationsAPI.sendToTopic({
          topic: formData.topic,
          title: formData.title,
          body: formData.body,
          type: formData.type,
        });
      }

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: `Notificação enviada com sucesso! ${response.data.data?.sent || 0} dispositivos notificados.` 
        });
        setFormData({
          target: 'all',
          userId: '',
          topic: '',
          title: '',
          body: '',
          type: 'SYSTEM',
          related_id: '',
        });
        loadLogs();
      } else {
        setMessage({ type: 'error', text: 'Erro ao enviar notificação' });
      }
    } catch (error) {
      console.error('Erro ao enviar notificação:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Erro ao enviar notificação' 
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      SENT: { text: 'Enviado', class: 'badge-success' },
      PENDING: { text: 'Pendente', class: 'badge-warning' },
      FAILED: { text: 'Falhou', class: 'badge-error' },
      PARTIAL: { text: 'Parcial', class: 'badge-warning' },
    };
    const badge = badges[status] || { text: status, class: 'badge-default' };
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  return (
    <AdminLayout>
      <div className="notifications-page">
        <div className="page-header">
          <h1><Bell size={26} /> Notificações Push</h1>
          <p>Envie notificações push para usuários via Firebase Cloud Messaging</p>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="notifications-container">
          {/* Formulário de Envio */}
          <div className="send-notification-card">
            <h2>Nova Notificação</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Destino</label>
                <select
                  value={formData.target}
                  onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                >
                  <option value="all">Todos os Usuários</option>
                  <option value="user">Usuário Específico</option>
                  <option value="topic">Tópico</option>
                </select>
              </div>

              {formData.target === 'user' && (
                <div className="form-group">
                  <label>Usuário</label>
                  <select
                    value={formData.userId}
                    onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                  >
                    <option value="">Selecione um usuário</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.target === 'topic' && (
                <div className="form-group">
                  <label>Tópico</label>
                  <input
                    type="text"
                    value={formData.topic}
                    onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                    placeholder="ex: all_users, premium_users"
                  />
                </div>
              )}

              <div className="form-group">
                <label>Título *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Título da notificação"
                  required
                />
              </div>

              <div className="form-group">
                <label>Corpo da Mensagem *</label>
                <textarea
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  placeholder="Mensagem da notificação"
                  rows="4"
                  required
                />
              </div>

              <div className="form-group">
                <label>Tipo</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="SYSTEM">Sistema</option>
                  <option value="ACHIEVEMENT">Conquista</option>
                  <option value="CHALLENGE">Desafio</option>
                  <option value="COMMUNITY">Comunidade</option>
                  <option value="MILESTONE">Marco</option>
                  <option value="RELAPSE_ALERT">Alerta de Recaída</option>
                </select>
              </div>

              <div className="form-group">
                <label>ID Relacionado (opcional)</label>
                <input
                  type="text"
                  value={formData.related_id}
                  onChange={(e) => setFormData({ ...formData, related_id: e.target.value })}
                  placeholder="ID do item relacionado (ex: challenge_id)"
                />
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Enviando...' : <><Send size={16} /> Enviar Notificação</>}
              </button>
            </form>
          </div>

          {/* Histórico de Logs */}
          <div className="logs-card">
            <div className="logs-header">
              <h2>Histórico de Envios</h2>
              <button onClick={loadLogs} className="btn btn-outline btn-sm" disabled={logsLoading}>
                {logsLoading ? 'Carregando...' : <><RefreshCw size={14} /> Atualizar</>}
              </button>
            </div>

            {logsLoading ? (
              <div className="loading">Carregando logs...</div>
            ) : logs.length === 0 ? (
              <div className="empty-state">Nenhum log encontrado</div>
            ) : (
              <div className="logs-table">
                <table>
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Título</th>
                      <th>Destino</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id}>
                        <td>
                          {new Date(log.created_at).toLocaleString('pt-BR')}
                        </td>
                        <td>{log.title}</td>
                        <td>
                          {log.target_type === 'ALL' ? 'Todos' : 
                           log.target_type === 'USER' ? `Usuário ${log.user_id || ''}` :
                           log.target_type === 'TOPIC' ? `Tópico ${log.topic || ''}` :
                           log.target_type}
                        </td>
                        <td>{getStatusBadge(log.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default NotificationsPage;

